/**
 * Section and attachment numbering.
 *
 * A block never knows that it happens to be § 5 — numbering is a property
 * of the assembled document, not of any block. Hiding, reordering or
 * inserting a section therefore renumbers everything downstream with no
 * block change, and cross-references follow because they are typed nodes
 * rather than literal text.
 */
const LETTERS = 'abcdefghijklmnopqrstuvwxyz';
export function formatOrdinal(position, format) {
    if (format === 'letter') {
        const index = position - 1;
        if (index < LETTERS.length)
            return LETTERS[index];
        return `${LETTERS[Math.floor(index / LETTERS.length) - 1]}${LETTERS[index % LETTERS.length]}`;
    }
    return String(position);
}
/** Default format for a given depth: 1 → "5", 2 → "5.2", 3 → "5.2.a". */
export const defaultFormatForDepth = (depth) => (depth >= 3 ? 'letter' : 'digit');
/**
 * Walks a part's body and stamps `number` + `path` onto every section
 * node. Returns the flat outline, in document order.
 *
 * A section with `numbering: 'none'` renders but consumes no number — the
 * address panel and the preamble are sections for anchoring and
 * referencing purposes, not numbered clauses.
 */
export function numberSections(nodes) {
    const outline = [];
    const walk = (list, depth, parentNumber, parentPath, parentFormat, parentInstance, parentScope, counter = { position: 0, scope: parentScope }) => {
        for (const node of list) {
            // A tracked review document keeps removed nodes as struck-through
            // ghosts; they must number exactly as if absent.
            if (node.edit?.change === 'removed')
                continue;
            const instanceId = ('instanceId' in node ? node.instanceId : undefined) ?? parentInstance;
            if (node.type !== 'section') {
                // Sections only nest directly; a section wrapped in a group still
                // numbers — and shares this level's sequence — so recurse through
                // the structural containers with the same counter.
                if (node.type === 'group' || node.type === 'notice') {
                    walk(node.content, depth, parentNumber, parentPath, parentFormat, instanceId, parentScope, counter);
                }
                continue;
            }
            const section = node;
            if (section.restartNumbering) {
                counter.position = 0;
                counter.scope = section.ref;
            }
            const scope = counter.scope;
            const path = [...parentPath, section.ref];
            if (section.numbering === 'none') {
                section.number = null;
                section.path = path;
            }
            else if (section.numbering === 'fixed') {
                // The number is part of the legal text. Leave it exactly as authored
                // and do not let it advance the automatic sequence.
                section.number = section.number ?? null;
                section.path = path;
            }
            else {
                counter.position += 1;
                const format = parentFormat ?? defaultFormatForDepth(depth);
                const own = formatOrdinal(counter.position, format);
                section.number = parentNumber ? `${parentNumber}.${own}` : own;
                section.path = path;
            }
            outline.push({
                ref: section.ref,
                number: section.number ?? null,
                ...(section.title ? { title: section.title } : {}),
                ...(instanceId ? { instanceId } : {}),
                ...(scope ? { scope } : {}),
            });
            walk(section.content, depth + 1, section.number ?? parentNumber, path, section.childFormat, instanceId, scope);
        }
    };
    walk(nodes, 1, null, [], undefined, undefined, undefined);
    return outline;
}
/**
 * Assigns "Bilaga N". A declaration with a `fixedNumber` claims that slot
 * (some statutes name the bilaga by number); everything else fills the
 * remaining positions in declaration order.
 */
export function numberAttachments(declarations) {
    const taken = new Set();
    for (const d of declarations)
        if (d.fixedNumber != null)
            taken.add(d.fixedNumber);
    let next = 1;
    const nextFree = () => {
        while (taken.has(next))
            next += 1;
        taken.add(next);
        return next;
    };
    return declarations.map((d) => ({ ...d, number: d.fixedNumber ?? nextFree() }));
}
//# sourceMappingURL=numbering.js.map